import { autoserializeAs } from 'cerialize';

// GET /donations/:id/chat passes Stream's QueryChannels response through verbatim, so these mirror Stream's
// message shape (snake_case on the wire), keeping only what the captain's read-only view renders.

// Chat user ids are minted by the API's Donations::ChatToken: `donor_<donationId>` / `driver_<userId>`.
export enum DonationChatRole {
  donor = 'donor',
  driver = 'driver',
}

export class DonationChatUser implements IDonationChatUser {
  @autoserializeAs('id') id?: string | null;
  @autoserializeAs('name') name?: string | null;

  constructor(payload?: IDonationChatUser) {
    this.id = payload?.id;
    this.name = payload?.name;
  }

  get role(): DonationChatRole | null {
    if (this.id?.startsWith(`${DonationChatRole.donor}_`)) return DonationChatRole.donor;
    if (this.id?.startsWith(`${DonationChatRole.driver}_`)) return DonationChatRole.driver;
    return null;
  }
}

export interface IDonationChatUser {
  id?: string | null;
  name?: string | null;
}

export class DonationChatAttachment implements IDonationChatAttachment {
  @autoserializeAs('type') type?: string | null;
  @autoserializeAs('image_url') imageUrl?: string | null;
  @autoserializeAs('thumb_url') thumbUrl?: string | null;

  constructor(payload?: IDonationChatAttachment) {
    this.type = payload?.type;
    this.imageUrl = payload?.imageUrl;
    this.thumbUrl = payload?.thumbUrl;
  }
}

export interface IDonationChatAttachment {
  type?: string | null;
  imageUrl?: string | null;
  thumbUrl?: string | null;
}

export class DonationChatMessage implements IDonationChatMessage {
  @autoserializeAs('id') id?: string | null;
  @autoserializeAs('text') text?: string | null;
  // Stream message type: regular | deleted | system | error | ephemeral | reply
  @autoserializeAs('type') type?: string | null;
  @autoserializeAs(DonationChatUser, 'user') user?: DonationChatUser | null;
  @autoserializeAs(DonationChatAttachment, 'attachments') attachments?: DonationChatAttachment[] | null;
  @autoserializeAs('created_at') createdAt?: string | null;
  @autoserializeAs('message_text_updated_at') messageTextUpdatedAt?: string | null;
  // a quoted reply (the driver app's swipe-to-reply): Stream sends the quoted message along with the id
  @autoserializeAs('quoted_message_id') quotedMessageId?: string | null;
  @autoserializeAs(DonationChatMessage, 'quoted_message') quotedMessage?: DonationChatMessage | null;

  constructor(payload?: IDonationChatMessage) {
    this.id = payload?.id;
    this.text = payload?.text;
    this.type = payload?.type;
    this.user = payload?.user ? new DonationChatUser(payload.user) : null;
    this.attachments = payload?.attachments?.map((a) => new DonationChatAttachment(a)) ?? [];
    this.createdAt = payload?.createdAt;
    this.messageTextUpdatedAt = payload?.messageTextUpdatedAt;
    this.quotedMessageId = payload?.quotedMessageId;
    this.quotedMessage = payload?.quotedMessage ? new DonationChatMessage(payload.quotedMessage) : null;
  }

  // one line for a quote block: the text, else what the attachment was
  get snippet(): string {
    if (this.deleted) return 'Message deleted';
    if (this.text) return this.text;
    return this.images.length ? 'Photo' : 'Attachment';
  }

  get deleted(): boolean {
    return this.type === 'deleted';
  }

  get images(): DonationChatAttachment[] {
    return (this.attachments ?? []).filter((a) => a.type === 'image' && !!(a.imageUrl || a.thumbUrl));
  }
}

export interface IDonationChatMessage {
  id?: string | null;
  text?: string | null;
  type?: string | null;
  user?: IDonationChatUser | null;
  attachments?: IDonationChatAttachment[] | null;
  createdAt?: string | null;
  messageTextUpdatedAt?: string | null;
  quotedMessageId?: string | null;
  quotedMessage?: IDonationChatMessage | null;
}

// One Stream channel. The API opens one per (donation, hauler partner) — `donation_<id>-partner_<partnerId>` — and
// adds every driver of that partner who chats to it, so a conversation is a hauler's, not a single driver's. A
// donation reassigned between haulers therefore holds several separate donor conversations.
export interface DonationChatConversation {
  id?: string | null;
  partnerId?: string | null;
  messages: DonationChatMessage[];
}
